import React, { useMemo, useState } from 'react';
import { TbReceipt2, TbUsers } from 'react-icons/tb';
import { FiArrowRight, FiLock, FiPlus, FiMinus } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { StatusBadge, statusBadgeTones } from '@/components/StatusBadge';

import styles from './CheckoutSeatsPlanModal.module.scss';

const getPricingForCurrency = (plan, currencyCode) =>
  plan?.pricing?.[currencyCode] || plan?.pricing?.USD || {};

const getUnitAmount = (plan, billingCycle, currencyCode) => {
  const pricing = getPricingForCurrency(plan, currencyCode);
  switch (billingCycle) {
    case 'quarterly':
      return pricing.quarterly;
    case 'yearly':
      return pricing.yearly;
    default:
      return pricing.monthly;
  }
};

const CYCLE_FACTOR = { monthly: 1, quarterly: 3, yearly: 12 };

export const CheckoutSeatsPlanModal = ({
  subscriptionData,
  plan,
  onClose,
  billingCycle,
  currency,
  onConfirm,
}) => {
  const seats = useMemo(() => {
    const q = subscriptionData?.occupiedSeats;
    if (q == null || q === '') return 2;
    const n = Number(q);
    return Number.isFinite(n) && n > 0 ? n : 2;
  }, [subscriptionData]);

  const minSeats = Math.max(2, seats);
  const [totalSeats, setTotalSeats] = useState(minSeats);

  const currencySymbol = currency === 'INR' ? '₹' : '$';
  const amount = getUnitAmount(plan, billingCycle, currency);
  const unitNum = amount != null ? Number(amount) : NaN;
  const totalAmount =
    Number.isFinite(unitNum) && totalSeats > 0 ? unitNum * totalSeats : null;

  const totalSavings = useMemo(() => {
    const monthlyPrice = getPricingForCurrency(plan, currency)?.monthly;
    if (!Number.isFinite(unitNum) || !Number.isFinite(monthlyPrice)) {
      return null;
    }
    const cycleFactor = CYCLE_FACTOR[billingCycle] || 1;
    const equivalentMonthlyTotal = monthlyPrice * cycleFactor;
    const savingsPerSeat = Math.max(equivalentMonthlyTotal - unitNum, 0);
    return savingsPerSeat * totalSeats;
  }, [plan, currency, unitNum, billingCycle, totalSeats]);

  const onConfirmClick = () => onConfirm(totalSeats);

  return (
    <div className={styles.modal}>
      <div className={styles.header}>
        <span className={styles.headerIcon}>
          <TbReceipt2 size={26} />
        </span>
        <div className={styles.headerText}>
          <Text
            variant={textVariantsEnum.Text_xl}
            fontWeight={fontWeightEnum.Bold}
            color="gray-900"
          >
            Total Amount for Subscription
          </Text>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-500"
          >
            Review your seats and see the total amount
          </Text>
        </div>
      </div>

      <div className={styles.card}>
        <span className={styles.cardIconSquare}>
          <TbUsers size={20} />
        </span>
        <div className={styles.cardText}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-600"
          >
            Current Seats
          </Text>
          <Text
            variant={textVariantsEnum.Text_xl}
            fontWeight={fontWeightEnum.Bold}
            color="gray-900"
          >
            {seats}
          </Text>
        </div>
        <StatusBadge
          label="Minimum 2 seats"
          tone={statusBadgeTones.purple}
          className={styles.cardBadge}
        />
      </div>

      <div className={styles.seatsSection}>
        <Text
          variant={textVariantsEnum.Text_md}
          fontWeight={fontWeightEnum.Semibold}
          color="gray-900"
        >
          Total Seats
        </Text>
        <div className={styles.stepper}>
          <button
            type="button"
            className={styles.stepperButton}
            disabled={totalSeats <= minSeats}
            onClick={() => setTotalSeats((s) => Math.max(minSeats, s - 1))}
            aria-label="Decrease seats"
          >
            <FiMinus size={16} />
          </button>
          <Text
            variant={textVariantsEnum.Text_xl}
            fontWeight={fontWeightEnum.Bold}
            color="gray-900"
          >
            {totalSeats}
          </Text>
          <button
            type="button"
            className={styles.stepperButton}
            onClick={() => setTotalSeats((s) => s + 1)}
            aria-label="Increase seats"
          >
            <FiPlus size={16} />
          </button>
        </div>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          You can update the seat count anytime from billing settings.
        </Text>
      </div>

      <div className={styles.card}>
        <span className={styles.cardIconCircle}>{currencySymbol}</span>
        <div className={styles.cardText}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Normal}
            color="gray-600"
          >
            Total Amount
          </Text>
          <Text
            variant={textVariantsEnum.Text_xl}
            fontWeight={fontWeightEnum.Bold}
            color="gray-900"
          >
            {totalAmount != null
              ? `${currencySymbol}${totalAmount.toFixed(2)}`
              : 'Price unavailable'}
          </Text>
          {totalAmount != null && (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              ({currencySymbol}
              {Number.isFinite(unitNum) ? unitNum.toFixed(2) : '—'} ×{' '}
              {totalSeats} seat{totalSeats !== 1 ? 's' : ''})
            </Text>
          )}
        </div>
        {totalSavings != null && (
          <div className={styles.savingsBlock}>
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Total savings
            </Text>
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Semibold}
              color="success-700"
            >
              {currencySymbol}
              {totalSavings.toFixed(2)}
            </Text>
          </div>
        )}
      </div>

      <div className={styles.buttonsContainer}>
        <Button
          variant={buttonVariants.secondary}
          text="Cancel"
          onClick={onClose}
        />
        <Button
          variant={buttonVariants.primary}
          text="Proceed to Pay"
          icon={<FiArrowRight />}
          iconPosition="right"
          onClick={onConfirmClick}
        />
      </div>

      <div className={styles.secureNote}>
        <FiLock size={12} />
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
          color="gray-500"
        >
          Secure payment. You can cancel anytime.
        </Text>
      </div>
    </div>
  );
};
